'use client';

import { useState, useEffect } from 'react';
import { Link, useRouter } from '@/i18n/navigation';
import { createClient } from '@/lib/db/supabase-client';
import { useSearchParams } from 'next/navigation';
import { ShieldCheck } from '@phosphor-icons/react';

interface AuthFormProps {
  mode: 'login' | 'signup';
}

// Proper Google & Apple SVG icons
function GoogleIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}

export default function AuthForm({ mode }: AuthFormProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const supabase = createClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  // KAN-322: Track activation verification for signup mode
  const [activationVerified, setActivationVerified] = useState(false);
  const [activationChecking, setActivationChecking] = useState(false);

  const isLogin = mode === 'login';

  useEffect(() => {
    if (isLogin) {
      const msg = searchParams.get('message');
      const err = searchParams.get('error');
      if (msg === 'check_email') {
        setMessage('Check your email to confirm your account');
      }
      if (msg === 'email_confirmed') {
        setMessage('Email confirmed! Please log in to continue.');
      }
      if (err === 'auth_failed') {
        setError('Authentication failed. Please try again.');
      }
    }
  }, [searchParams, isLogin]);

  // KAN-322: Verify activation cookie on mount for signup mode.
  // This is a client-side defense-in-depth; the middleware also blocks
  // /signup without an activation cookie.
  useEffect(() => {
    if (isLogin) {
      setActivationVerified(true); // login mode doesn't need activation
      return;
    }

    let cancelled = false;
    setActivationChecking(true);

    fetch('/api/activate/status')
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        if (data.activated) {
          setActivationVerified(true);
        } else {
          setError('You need an activation code to sign up. Redirecting…');
          window.location.href = '/activate';
        }
      })
      .catch(() => {
        if (cancelled) return;
        // On network error, redirect to activate as a safe default
        window.location.href = '/activate';
      })
      .finally(() => {
        if (!cancelled) setActivationChecking(false);
      });

    return () => { cancelled = true; };
  }, [isLogin]);

  const handleOAuth = async (provider: 'google' | 'apple') => {
    try {
      setLoading(true);
      setError('');

      // KAN-322: Block if activation not verified (defense-in-depth)
      if (!isLogin && !activationVerified) {
        setError('You need an activation code to sign up. Redirecting…');
        window.location.href = '/activate';
        setLoading(false);
        return;
      }

      // Persist marketing consent through OAuth redirect via cookie
      if (!isLogin && marketingConsent) {
        document.cookie = 'vetta_marketing_consent=true; path=/; max-age=600; SameSite=Lax';
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/callback`,
        },
      });
      if (error) setError(error.message);
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    if (!email || !password) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    try {
      if (isLogin) {
        const { error: loginError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (loginError) {
          setError(loginError.message);
        } else {
          // KAN-280: Sync user record post-login (handles cross-device email
          // confirmation where the callback couldn't create the user record).
          try {
            const syncRes = await fetch('/api/auth/sync-user', { method: 'POST' });
            const syncData = await syncRes.json();
            if (syncData.needsOnboarding) {
              router.push('/onboarding/dog');
              return;
            }
            if (!syncData.isActivated) {
              router.push('/activate');
              return;
            }
          } catch {
            // Non-blocking — fall through to chat
          }
          router.push('/chat');
        }
      } else {
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        if (password.length < 8) {
          setError('Password must be at least 8 characters');
          setLoading(false);
          return;
        }

        // KAN-322: Block if activation not verified (defense-in-depth)
        if (!activationVerified) {
          setError('You need an activation code to sign up. Redirecting…');
          window.location.href = '/activate';
          setLoading(false);
          return;
        }

        // Re-fetch activation status to get codeId for user metadata
        const cookieCheck = await fetch('/api/activate/status');
        const cookieData = await cookieCheck.json();

        // Persist marketing consent through email confirmation redirect via cookie
        if (marketingConsent) {
          document.cookie = 'vetta_marketing_consent=true; path=/; max-age=86400; SameSite=Lax';
        }

        // KAN-273: Pass activation code ID in user metadata so the callback
        // can read it even if the email confirmation is opened on a different device.
        const { error: signupError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/callback`,
            data: {
              ...(cookieData.codeId ? { activation_code_id: cookieData.codeId } : {}),
            },
          },
        });
        if (signupError) {
          setError(signupError.message);
        } else {
          router.push('/login?message=check_email');
        }
      }
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 w-full max-w-sm mx-auto">
      {/* Header */}
      <div className="text-center">
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-dark mb-2">
          {isLogin ? 'Welcome back' : 'Create your account'}
        </h1>
        <p className="text-gray-500 text-sm">
          {isLogin
            ? 'Sign in to continue your dog\'s health journey'
            : 'Create your account to get started'}
        </p>
      </div>

      {/* Status messages */}
      {error && (
        <div className="p-3 bg-error-light border border-red-200 rounded-brand text-error text-sm">
          {error}
        </div>
      )}
      {message && (
        <div className="p-3 bg-success-light border border-green-200 rounded-brand text-success text-sm">
          {message}
        </div>
      )}

      {/* OAuth — prominent, first */}
      <div className="space-y-3">
        <button
          onClick={() => handleOAuth('google')}
          disabled={loading || activationChecking || (!isLogin && !activationVerified)}
          className="w-full py-3 px-4 bg-surface border-2 border-gray-300 rounded-brand font-medium text-dark hover:bg-cream hover:border-gray-400 disabled:opacity-50 transition-all flex items-center justify-center gap-3 min-h-[48px] shadow-sm"
        >
          <GoogleIcon />
          Continue with Google
        </button>

        <button
          onClick={() => handleOAuth('apple')}
          disabled={loading || activationChecking || (!isLogin && !activationVerified)}
          className="w-full py-3 px-4 bg-black text-white rounded-brand font-medium hover:bg-gray-900 disabled:opacity-50 transition-all flex items-center justify-center gap-3 min-h-[48px]"
        >
          <AppleIcon />
          Continue with Apple
        </button>
      </div>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-3 bg-cream text-gray-400">or</span>
        </div>
      </div>

      {/* Email form — secondary */}
      <form onSubmit={handleEmailSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-600 mb-1.5">
            Email
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full px-4 py-3 border border-border rounded-brand focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-surface text-dark min-h-[48px]"
            disabled={loading}
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-600 mb-1.5">
            Password
          </label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={isLogin ? 'Enter your password' : 'At least 8 characters'}
            className="w-full px-4 py-3 border border-border rounded-brand focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-surface text-dark min-h-[48px]"
            disabled={loading}
          />
        </div>

        {!isLogin && (
          <div>
            <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-600 mb-1.5">
              Confirm password
            </label>
            <input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm password"
              className="w-full px-4 py-3 border border-border rounded-brand focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-surface text-dark min-h-[48px]"
              disabled={loading}
            />
          </div>
        )}

        {!isLogin && (
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={marketingConsent}
              onChange={(e) => setMarketingConsent(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-border text-teal-500 focus:ring-teal-500 accent-teal-500"
              disabled={loading}
            />
            <span className="text-sm text-gray-500 leading-snug">
              I&apos;d like to receive tips, product updates and offers from Vetta. You can unsubscribe anytime.
            </span>
          </label>
        )}

        {isLogin && (
          <div className="text-right">
            <a href="#" className="text-sm text-teal-500 hover:text-teal-600 font-medium">
              Forgot password?
            </a>
          </div>
        )}

        <button
          type="submit"
          disabled={loading || activationChecking || (!isLogin && !activationVerified)}
          className="w-full py-3 px-4 bg-teal-500 text-white font-heading font-semibold rounded-brand hover:bg-teal-600 disabled:bg-gray-400 transition-colors min-h-[48px]"
        >
          {activationChecking
            ? 'Verifying…'
            : loading
              ? (isLogin ? 'Signing in...' : 'Creating account...')
              : (isLogin ? 'Sign in' : 'Create account')}
        </button>
      </form>

      {/* Trust shield */}
      <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
        <ShieldCheck weight="regular" className="w-4 h-4 text-teal-500" />
        <span>Data stored in the EU</span>
      </div>

      {/* Switch mode link */}
      <div className="text-center text-sm">
        <span className="text-gray-500">
          {isLogin ? 'Don\'t have an account? ' : 'Already have an account? '}
        </span>
        <Link
          href={isLogin ? '/signup' : '/login'}
          className="text-teal-500 font-medium hover:text-teal-600"
        >
          {isLogin ? 'Sign up' : 'Log in'}
        </Link>
      </div>
    </div>
  );
}
